import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DatePipe, NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { LucideAngularModule, Mail, X } from 'lucide-angular';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { Destroyer } from '@app/shared/abstract/destroyer';
import { FeatureToggleService } from '@app/feature-toggles/feature-toggle.service';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { VenueService } from '@app/venues/shared/venue.service';
import {
  Communication,
  CommunicationChannel,
  CommunicationKindFilter,
} from './communication.model';
import { CommunicationService } from './communication.service';
import { ComposeEmailModalComponent } from './compose-email-modal.component';

/**
 * TVG-343: the Communications page.
 *
 * An inbox and a sent box in one place, so staff do not open each contact in
 * turn to see what has been said. Deliberately a list and not a conversation
 * view: a line is one message, and clicking a line reads it in the panel.
 *
 * Texts and received messages have their own filters and render properly if
 * rows ever carry them, but nothing in this case writes either — sending texts
 * is a separate case and replies are TVG-342. Until those land the page shows
 * what the platform has sent and what staff have written.
 *
 * The whole page sits behind the `communications` toggle, off by default on
 * production, and the route guard and the server agree on that.
 */
@Component({
  selector: 'app-communications-page',
  standalone: true,
  imports: [
    DatePipe,
    NgClass,
    ReactiveFormsModule,
    RouterLink,
    LucideAngularModule,
    SelectComponent,
    TextInputComponent,
    ComposeEmailModalComponent,
  ],
  templateUrl: './communications-page.component.html',
})
export class CommunicationsPageComponent extends Destroyer implements OnInit, AfterViewInit, OnDestroy {
  /** Sentinel below the last line; scrolling it into view fetches the next page. */
  @ViewChild('scrollSentinel') private scrollSentinel?: ElementRef<HTMLElement>;
  @ViewChild(ComposeEmailModalComponent) private composeModal?: ComposeEmailModalComponent;

  protected readonly MailIcon = Mail;
  protected readonly XIcon = X;

  protected readonly channelChoices: { value: CommunicationChannel | null; label: string }[] = [
    { value: null, label: 'Email and text' },
    { value: 'email', label: 'Email' },
    { value: 'text', label: 'Text' },
  ];

  private static readonly ALL_KIND_CHOICES: { value: CommunicationKindFilter | null; label: string }[] = [
    { value: null, label: 'All' },
    { value: 'sent', label: 'Sent' },
    { value: 'received', label: 'Received' },
    { value: 'automatic', label: 'Automatic' },
    { value: 'not_filed', label: 'Not filed' },
  ];

  /**
   * TVG-386: Automatic is noise most staff never need, so it is hidden unless
   * the `communications-automatic` toggle is on. Sent and Received stay
   * visible either way.
   */
  protected readonly kindChoices: { value: CommunicationKindFilter | null; label: string }[];

  protected messages: Communication[] = [];
  protected venueChoices: { value: number | null; label: string }[] = [];
  protected selectedMessage: Communication | null = null;

  protected composeOpen = false;
  protected sentNotice: string | null = null;

  protected resending = false;
  protected resendError: string | null = null;

  protected loading = false;
  protected loadError: string | null = null;
  protected hasLoadedOnce = false;
  protected total = 0;

  protected channel: CommunicationChannel | null = null;
  /** TVG-386: the page opens on Received, not All. */
  protected kind: CommunicationKindFilter | null = 'received';

  protected searchControl = new FormControl('');
  protected venueControl = new FormControl<number | null>(null);

  private currentPage = 1;
  private lastPage = 1;
  private observer?: IntersectionObserver;

  private readonly pageSize = 50;

  constructor(
    private communicationService: CommunicationService,
    private venueService: VenueService,
    private featureToggleService: FeatureToggleService,
  ) {
    super();

    this.kindChoices = CommunicationsPageComponent.ALL_KIND_CHOICES.filter(
      (choice) => choice.value !== 'automatic' || this.featureToggleService.isEnabled('communications-automatic'),
    );
  }

  ngOnInit(): void {
    this.loadVenues();
    this.load();

    // Debounced so a search is one request per pause in typing, not per key.
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.reload());

    this.venueControl.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.reload());
  }

  ngAfterViewInit(): void {
    if (!this.scrollSentinel) {
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadMore();
        }
      },
      { rootMargin: '200px' },
    );

    this.observer.observe(this.scrollSentinel.nativeElement);
  }

  override ngOnDestroy(): void {
    this.observer?.disconnect();
    super.ngOnDestroy();
  }

  protected get hasMore(): boolean {
    return this.currentPage < this.lastPage;
  }

  protected get isEmpty(): boolean {
    return this.hasLoadedOnce && !this.loading && this.messages.length === 0;
  }

  protected get isFiltered(): boolean {
    return (
      this.channel !== null ||
      this.kind !== null ||
      this.venueControl.value != null ||
      !!this.searchControl.value?.trim()
    );
  }

  protected setChannel(channel: CommunicationChannel | null): void {
    this.channel = channel;
    this.reload();
  }

  protected setKind(kind: CommunicationKindFilter | null): void {
    this.kind = kind;
    this.reload();
  }

  protected openMessage(message: Communication): void {
    this.resendError = null;
    this.selectedMessage = message;
  }

  protected closeMessage(): void {
    this.resendError = null;
    this.selectedMessage = null;
  }

  protected openCompose(): void {
    this.sentNotice = null;
    this.composeOpen = true;
    this.composeModal?.reset();
  }

  protected closeCompose(): void {
    this.composeOpen = false;
  }

  /**
   * The queued message goes straight onto the top of the list, so the staff
   * member sees what they just sent without reloading. It is genuinely Queued
   * at this point — it settles to Sent once the queue has delivered it, which
   * the next refresh of the list picks up.
   */
  protected onSent(message: Communication): void {
    this.composeOpen = false;
    this.sentNotice = 'Queued. It sends in the background, so you do not have to wait.';
    this.messages = [message, ...this.messages];
    this.total += 1;
  }

  /**
   * Send a failed email again, as the message panel offers.
   *
   * The server reuses the same row, so the line already on the list is replaced
   * in place rather than a second one appearing for what staff think of as one
   * message.
   */
  protected resend(message: Communication): void {
    if (this.resending) {
      return;
    }

    this.resending = true;
    this.resendError = null;

    this.communicationService.resend(message.id).subscribe({
      next: ({ data }) => {
        this.resending = false;
        this.selectedMessage = data;
        this.messages = this.messages.map((row) => (row.id === data.id ? data : row));
        this.sentNotice = 'Queued again. It sends in the background.';
      },
      error: (error: HttpErrorResponse) => {
        this.resending = false;
        this.resendError = error?.error?.message ?? 'It could not be sent again. Please try once more.';
      },
    });
  }

  protected statusClasses(message: Communication): string {
    switch (message.status) {
      case 'queued':
        return 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300';
      case 'failed':
      case 'bounced':
        return 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300';
      case 'not_filed':
        return 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300';
      case 'received':
        return 'bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-white/70';
      default:
        return 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300';
    }
  }

  protected loadMore(): void {
    if (!this.hasMore || this.loading) {
      return;
    }

    this.load(this.currentPage + 1);
  }

  protected retry(): void {
    this.load(this.currentPage);
  }

  /** A filter change restarts the list from page one. */
  private reload(): void {
    this.currentPage = 1;
    this.lastPage = 1;
    this.load();
  }

  private load(pageNum = 1): void {
    this.loading = true;
    this.loadError = null;

    this.communicationService
      .list({
        channel: this.channel,
        kind: this.kind,
        venueId: this.venueControl.value,
        search: this.searchControl.value?.trim() || null,
        pageNum,
        pageSize: this.pageSize,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const rows = response.data ?? [];
          this.messages = pageNum === 1 ? rows : [...this.messages, ...rows];
          this.currentPage = response.meta?.current_page ?? pageNum;
          this.lastPage = response.meta?.last_page ?? pageNum;
          this.total = response.meta?.total ?? this.messages.length;
          this.loading = false;
          this.hasLoadedOnce = true;
        },
        error: (error) => {
          this.loading = false;
          this.hasLoadedOnce = true;
          this.loadError = error?.error?.message || 'Failed to load messages';
        },
      });
  }

  private loadVenues(): void {
    this.venueService
      .getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.venueChoices = (response?.data ?? []).map((venue: { id: number; name: string }) => ({
            value: venue.id,
            label: venue.name,
          }));
        },
        // A venue list that fails to load costs the filter, not the page.
        error: () => {
          this.venueChoices = [];
        },
      });
  }
}
